// DataTable — generic grid table: column defs drive the header + cell renders,
// with an optional checkbox lead (lockable per row), controlled sorting
// affordances, hover/click rows, opt-in expandable rows (trailing chevron +
// full-width detail panel), opt-in grouping (caller-defined group keys →
// full-width header bands), and loading/error/empty states. The caller owns
// cell content and data fetching; this owns structure only.

import { Fragment, type CSSProperties, type ReactNode } from 'react';
import Icon from './Icon';

export interface DataTableColumn<T> {
  key: string;
  label: ReactNode;
  /** Grid track, e.g. 'minmax(14rem, 1.6fr)' or '7.5rem'. Default 'auto':
   *  the column hugs its content (never narrower — the table scrolls instead).
   *  minmax(floor, fr) columns DO compress to their floor, so their text
   *  needs a truncation chain to ellipsize. */
  width?: string;
  sortable?: boolean;
  /** Inline alignment of the header + cell content. Default 'start'. */
  align?: 'start' | 'end';
  render: (row: T) => ReactNode;
}

/** One group definition — nothing here is DataTable's: the key set, order,
 *  labels and dot colors are all the caller's (predefined enum values). */
export interface DataTableGroup {
  key: string;
  /** Band text, already translated. */
  label: ReactNode;
  /** Band dot (a semantic token var); omitted = no dot. */
  dotColor?: string;
}

export interface DataTableGrouping<T> {
  /** Ordered group definitions — band order follows this array. */
  groups: DataTableGroup[];
  /** Resolves a row's group key. */
  groupOf: (row: T) => string;
  /** Row ids excluded from grouping — rendered first, above the bands, in
   *  their incoming order (e.g. pinned system rows). */
  excludeIds?: string[];
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Render the checkbox lead column. */
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  /** Header "select all" checkbox — rendered only when `onToggleSelectAll` is
   *  given (and `selectable`). `allSelected` fills it, `someSelected` (without
   *  all) shows the indeterminate dash. */
  allSelected?: boolean;
  someSelected?: boolean;
  onToggleSelectAll?: () => void;
  /** Accessible name for the header select-all checkbox. */
  selectAllLabel?: string;
  /** Row shows a lock instead of its checkbox (e.g. default/system rows). */
  isRowLocked?: (row: T) => boolean;
  /** Row renders dimmed — for records that are hidden/retired but still listed. */
  isRowMuted?: (row: T) => boolean;
  /** Row renders highlighted — for the caller's current selection (e.g. the
   *  timesheet day the detail pane is showing). Opt-in, like isRowMuted. */
  isRowActive?: (row: T) => boolean;
  /** Row renders danger-tinted — for records that failed validation but stay
   *  listed (e.g. flagged CSV-import rows). Opt-in, like isRowActive. */
  isRowError?: (row: T) => boolean;
  /** Controlled sort; header click fires onSort(key) only when the column is
   *  sortable AND onSort is provided — otherwise glyphs render inert. */
  sort?: { key: string; type: 'asc' | 'desc' };
  onSort?: (key: string) => void;
  loading?: boolean;
  error?: boolean;
  /** Custom empty-state node (icon + text). */
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  /** Expandable rows — providing renderExpanded appends a trailing chevron
   *  column and renders this node full-width below the expanded row. The open
   *  state is controlled (single-expanded accordion), the sort/onSort idiom:
   *  the toggle stopPropagations, so a caller may ALSO pass an onRowClick that
   *  toggles the same id without double-firing. */
  renderExpanded?: (row: T) => ReactNode;
  expandedId?: string | null;
  onToggleExpand?: (id: string) => void;
  /** Accessible name for the per-row expand toggle. */
  expandLabel?: string;
  /** Opt-in grouping — DataTable distributes `rows` under the caller's ordered
   *  group definitions and draws a full-width header band (dot + label +
   *  count) per non-empty group. Excluded ids lead, bandless; rows whose key
   *  matches no definition trail, bandless (never dropped). Selection,
   *  locking, sorting and expansion are untouched — grouping only reorders. */
  grouping?: DataTableGrouping<T>;
  /** Row height. 'compact' tightens row padding only — every other measure,
   *  including the header, is unchanged. Opt-in: the default matches every
   *  existing call site. */
  density?: 'default' | 'compact';
  /** Horizontal-scroll floor (px) for the grid. Default 900. */
  minWidth?: number;
  /** Fallback texts for loading/error states (already translated). */
  loadingText?: string;
  errorText?: string;
}

const DataTable = <T,>({
  columns,
  rows,
  rowKey,
  selectable = false,
  selectedIds,
  onToggleSelect,
  allSelected = false,
  someSelected = false,
  onToggleSelectAll,
  selectAllLabel,
  isRowLocked,
  isRowMuted,
  isRowActive,
  isRowError,
  sort,
  onSort,
  loading = false,
  error = false,
  empty,
  onRowClick,
  renderExpanded,
  expandedId = null,
  onToggleExpand,
  expandLabel,
  grouping,
  density = 'default',
  minWidth = 900,
  loadingText,
  errorText,
}: DataTableProps<T>) => {
  // Columns default to content-sized `auto` tracks — the header + rows render
  // as subgrids of one outer grid, so sizing is shared and stays aligned.
  // `width` is an optional override (fixed or fr tracks) per column.
  const expandable = !!renderExpanded;
  const tracks = [
    ...(selectable ? ['min-content'] : []),
    ...columns.map((c) => c.width ?? 'auto'),
    ...(expandable ? ['min-content'] : []),
  ].join(' ');
  const gridStyle = { '--dt-cols': tracks, '--dt-min': `${minWidth}px` } as CSSProperties;

  // Distribute the rows into render sections: without grouping, one bandless
  // section; with it, excluded rows first, then the defined groups in order
  // (each keeping its rows' incoming order), then any unmatched rows —
  // trailing and bandless rather than silently dropped.
  type Section = { band?: { group: DataTableGroup; count: number }; rows: T[] };
  let sections: Section[];
  if (grouping) {
    const excluded = new Set(grouping.excludeIds ?? []);
    const top: T[] = [];
    const rest: T[] = [];
    const buckets = new Map<string, T[]>(grouping.groups.map((g) => [g.key, []]));
    rows.forEach((row) => {
      if (excluded.has(rowKey(row))) top.push(row);
      else (buckets.get(grouping.groupOf(row)) ?? rest).push(row);
    });
    sections = [
      { rows: top },
      ...grouping.groups
        .map((group) => ({ group, bucket: buckets.get(group.key) ?? [] }))
        .filter(({ bucket }) => bucket.length > 0)
        .map(({ group, bucket }) => ({ band: { group, count: bucket.length }, rows: bucket })),
      { rows: rest },
    ];
  } else {
    sections = [{ rows }];
  }

  const sortIcon = (col: DataTableColumn<T>) => {
    if (!col.sortable) return null;
    const active = sort?.key === col.key;
    const name = !active
      ? 'chevrons-up-down'
      : sort?.type === 'asc'
        ? 'chevron-up'
        : 'chevron-down';
    return <Icon name={name} size={13} />;
  };

  const renderRow = (row: T) => {
    const id = rowKey(row);
    const locked = isRowLocked?.(row) ?? false;
    const open = expandable && expandedId === id;
    return (
      <Fragment key={id}>
        <div
          className={`data-table__row${onRowClick ? ' data-table__row--clickable' : ''}${
            isRowMuted?.(row) ? ' data-table__row--muted' : ''
          }${isRowActive?.(row) ? ' data-table__row--active' : ''}${
            isRowError?.(row) ? ' data-table__row--error' : ''
          }${open ? ' data-table__row--expanded' : ''}`}
          {...(onRowClick
            ? {
                role: 'button',
                tabIndex: 0,
                onClick: () => onRowClick(row),
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(row);
                  }
                },
              }
            : {})}
        >
          {selectable && (
            <span className='data-table__lead'>
              {locked ? (
                <Icon name='lock' size={15} className='data-table__lock' />
              ) : (
                <label
                  className='app-checkbox app-checkbox--sm'
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type='checkbox'
                    className='app-checkbox__input'
                    checked={selectedIds?.has(id) ?? false}
                    onChange={() => onToggleSelect?.(id)}
                  />
                  <span className='app-checkbox__box' aria-hidden='true'>
                    <Icon name='check' size={12} className='app-checkbox__check' />
                  </span>
                </label>
              )}
            </span>
          )}
          {columns.map((col) => (
            <span
              key={col.key}
              className={`data-table__cell${col.align === 'end' ? ' data-table__cell--end' : ''}`}
            >
              {col.render(row)}
            </span>
          ))}
          {expandable && (
            <span className='data-table__expand'>
              <button
                type='button'
                className={`data-table__expand-toggle${open ? ' is-open' : ''}`}
                aria-expanded={open}
                aria-label={expandLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand?.(id);
                }}
              >
                <Icon name='chevron-down' size={16} />
              </button>
            </span>
          )}
        </div>
        {open && renderExpanded && (
          <div className='data-table__expansion'>{renderExpanded(row)}</div>
        )}
      </Fragment>
    );
  };

  return (
    <div
      className={`data-table${density === 'compact' ? ' data-table--compact' : ''}`}
      style={gridStyle}
    >
      <div className='data-table__grid'>
        <div className='data-table__head'>
          {selectable &&
            (onToggleSelectAll ? (
              <SelectAllCheckbox
                allSelected={allSelected}
                someSelected={someSelected}
                onToggle={onToggleSelectAll}
                label={selectAllLabel}
              />
            ) : (
              <span />
            ))}
          {columns.map((col) => {
            const interactive = !!col.sortable && !!onSort;
            const endClass = col.align === 'end' ? ' data-table__col--end' : '';
            return interactive ? (
              <button
                key={col.key}
                type='button'
                className={`data-table__col data-table__col--sortable${endClass}${
                  sort?.key === col.key ? ' is-sorted' : ''
                }`}
                onClick={() => onSort(col.key)}
              >
                {col.label}
                {sortIcon(col)}
              </button>
            ) : (
              <span key={col.key} className={`data-table__col${endClass}`}>
                {col.label}
                {sortIcon(col)}
              </span>
            );
          })}
          {expandable && <span />}
        </div>

        {!loading &&
          !error &&
          rows.length > 0 &&
          sections.map((section, index) => (
            <Fragment key={section.band?.group.key ?? `ungrouped-${index}`}>
              {section.band && (
                <div className='data-table__group'>
                  {section.band.group.dotColor && (
                    <span
                      className='data-table__group-dot'
                      style={{ backgroundColor: section.band.group.dotColor }}
                      aria-hidden='true'
                    />
                  )}
                  <span className='data-table__group-label'>{section.band.group.label}</span>
                  <span className='data-table__group-count'>{section.band.count}</span>
                </div>
              )}
              {section.rows.map(renderRow)}
            </Fragment>
          ))}
      </div>

      {loading ? (
        <div className='data-table__status'>{loadingText}</div>
      ) : error ? (
        <div className='data-table__status'>{errorText}</div>
      ) : rows.length === 0 ? (
        <div className='data-table__status'>{empty}</div>
      ) : null}
    </div>
  );
};

/** Header select-all checkbox — reuses the shared .app-checkbox visuals; a
 *  callback ref drives the native `indeterminate` flag (no DOM attribute
 *  exists for it). */
const SelectAllCheckbox = ({
  allSelected,
  someSelected,
  onToggle,
  label,
}: {
  allSelected: boolean;
  someSelected: boolean;
  onToggle: () => void;
  label?: string;
}) => {
  return (
    <label className='app-checkbox app-checkbox--sm data-table__select-all'>
      <input
        // Synced during commit (callback refs run on every committed render)
        // rather than in a passive effect: Chrome stops matching :checked
        // while `indeterminate` is true, so a late effect write lets a
        // checked-but-still-indeterminate frame paint as the dash.
        ref={(el) => {
          if (el) el.indeterminate = someSelected && !allSelected;
        }}
        type='checkbox'
        className='app-checkbox__input'
        checked={allSelected}
        onChange={onToggle}
        aria-label={label}
      />
      <span className='app-checkbox__box' aria-hidden='true'>
        <Icon name='check' size={12} className='app-checkbox__check' />
        <Icon name='minus' size={12} className='app-checkbox__dash' />
      </span>
    </label>
  );
};

export default DataTable;
